import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useOmniAuth } from '@omni/auth'
import { shapeProgramme, type Programme } from './shape'

export { askIn, projectIn, reportIn, type Programme } from './shape'

// The Programme facts, read from Nateva's own database (remaining work 3.2;
// nateva-platform supabase/migrations/0019_programme_live.sql), through
// the SHELL SESSION like every Programme read. The row rules let only people
// on the programme list read them. Pages show; every change is made by
// asking Claude (nateva-platform scripts/programme/programme.mjs).
//
//   programme_projects  each project: lead, stage, what it is, what is next
//   programme_stages    the six stages, in order
//   programme_updates   dated updates per project, newest first
//   programme_asks      what the programme waits on from Nateva
//   programme_plan      the plan steps
//   programme_reports   reports, opened in place
//   programme_repos     each project's repositories
//   programme_changes   the edit log; its newest edit by a person is "Updated"
//   programme_tasks     the board: every piece of work, by project and status
//
// Archived rows never show.

const TABLES = {
  projects: ['programme_projects', 'slug, sort, name, front_door_app, lead_name, lead_email, replaces, what_text, stage, next_step, waiting_on, archived_at'],
  stages: ['programme_stages', 'key, sort, label'],
  updates: ['programme_updates', 'project_slug, on_date, text, archived_at'],
  asks: ['programme_asks', 'key, sort, title, text, detail, owner, asked_on, due_on, status, archived_at'],
  plan: ['programme_plan', 'sort, label, starts_on, title, items, detail, archived_at'],
  reports: ['programme_reports', 'id, project_slug, title, kind, on_date, pages, line, readers, file_path, builtin, archived_at'],
  repos: ['programme_repos', 'project_slug, repo, archived_at'],
  changes: ['programme_changes', 'at, signed_in_email, project_slug'],
  tasks: ['programme_tasks', 'id, no, project_slug, title, detail, status, owner_name, owner_side, waiting_on, due_on, started_on, done_on, sort, archived_at'],
} as const

type Row = Record<string, unknown>
type Res = { data: Row[] | null; error: { message: string } | null }
type Db = { from(t: string): { select(c: string): PromiseLike<Res> & { order(c: string, o: { ascending: boolean }): { limit(n: number): PromiseLike<Res> } } } }

/** Every Programme fact, from the database. The edit log and the board are optional: if either cannot be read, the rest still shows. */
export function useProgramme() {
  const { getClient } = useOmniAuth()
  const db = useMemo(() => getClient('crm').schema('public') as unknown as Db, [getClient])
  return useQuery<Programme>({
    queryKey: ['nateva', 'programme'],
    queryFn: async () => {
      const keys = Object.keys(TABLES) as (keyof typeof TABLES)[]
      const optional = (k: string) => k === 'changes' || k === 'tasks'
      const results = await Promise.all(
        keys.map((k) => {
          const [table, cols] = TABLES[k]
          return k === 'changes'
            ? db.from(table).select(cols).order('at', { ascending: false }).limit(200)
            : db.from(table).select(cols)
        }),
      )
      const rows = {} as Record<keyof typeof TABLES, Row[]>
      keys.forEach((k, i) => {
        const r = results[i]
        if (r.error && !optional(k)) throw new Error(r.error.message)
        rows[k] = r.error ? [] : (r.data ?? [])
      })
      return shapeProgramme(rows)
    },
    refetchInterval: 5 * 60_000,
  })
}

